import { ChangeDetectionStrategy, Component, inject, OnInit, OnDestroy, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDivider } from '@angular/material/divider';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../../ui/icon/icon.component';
import { AuthService } from '../../../../core/services/auth.service';
import { NotificationService } from '../../../../core/services/notification.service';
import { NotificationPanelComponent } from '../../notification-panel/notification-panel.component';
import { Subject, of, takeUntil } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError, tap } from 'rxjs/operators';
import { SearchService } from '../../../../core/services/search.service';
import { SearchResponse } from '../../../../core/models/search.models';
import { SupportService } from '../../../../core/services/support.service';
import { ActiveOrgService } from '../../../../core/services/active-org.service';
import { ActiveOrg, SupportAccessOut } from '../../../../core/models/support.models';

interface SearchSuggestion {
  entityType: string;
  entityId: string;
  displayText: string;
  typeLabel: string;
  iconName: string;
}

const TYPE_LABELS: Record<string, string> = {
  legal_entity: 'Юридична особа',
  property: 'Нерухомість',
  vehicle: 'Транспорт',
  official: 'Посадова особа',
};

const TYPE_ORDER = ['legal_entity', 'property', 'vehicle', 'official'];

const DETAIL_ROUTES: Record<string, string> = {
  legal_entity: '/legal-entity-details',
  property: '/property-details',
  vehicle: '/transport-details',
  official: '/official-details',
};

// Мапінги «підтип → іконка» — дзеркалять таблиці списків (entity/property/transport-table),
// щоб іконка в пошуку збігалась з тим, що юзер бачить у відповідному реєстрі.
const LEGAL_ENTITY_ICONS: Record<string, string> = {
  'Комунальне підприємство': 'Building2',
  'Комунальне некомерційне підприємство': 'Building',
  'Комунальний заклад': 'Heart',
  'Комунальна установа': 'Users',
  'Інше': 'HelpCircle',
};

const PROPERTY_ICONS: Record<string, string> = {
  'Будівля': 'Building2',
  'Інше': 'Home',
  'Земельна ділянка': 'Mountain',
};

const VEHICLE_ICONS: Record<string, string> = {
  'Легковий': 'Car',
  'Вантажний': 'Truck',
  'Автобус': 'Bus',
  'Мікроавтобус': 'Bus',
  'Спецтехніка': 'Tractor',
  'Мотоцикл': 'Bike',
};

function iconFor(entityType: string, subtype: string | null): string {
  switch (entityType) {
    case 'legal_entity':
      return (subtype && LEGAL_ENTITY_ICONS[subtype]) || 'Building2';
    case 'property':
      return (subtype && PROPERTY_ICONS[subtype]) || 'Home';
    case 'vehicle':
      return (subtype && VEHICLE_ICONS[subtype]) || 'Car';
    case 'official':
      return 'User';
    default:
      return 'FileText';
  }
}

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    MatIcon,
    MatIconButton,
    MatButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatDivider,
    FormsModule,
    IconComponent,
    NotificationPanelComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Header implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private searchService = inject(SearchService);
  private supportService = inject(SupportService);
  private activeOrgService = inject(ActiveOrgService);
  private destroy$ = new Subject<void>();
  private searchInput$ = new Subject<string>();

  searchQuery = signal('');
  isSearchFocused = signal(false);
  showSuggestions = signal(false);
  searchResults = signal<SearchSuggestion[]>([]);
  isSearching = signal(false);
  currentLanguage = signal('Українська');
  notificationCount = signal(0);
  userName = signal('Адміністратор');
  pageTitle = signal('Панель управління');
  showNotificationPanel = signal(false);

  // SUPPORT: перемикач організацій (показується лише для ролі SUPPORT)
  isSupport = signal(false);
  organizations = signal<SupportAccessOut[]>([]);
  activeOrg = this.activeOrgService.activeOrg;

  menuToggle = output<void>();

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    // Update page title based on route
    this.updatePageTitle();
  }

  ngOnInit(): void {
    // Subscribe to unread count
    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.notificationCount.set(count);
      });

    // Connect to SSE and load initial data
    this.notificationService.connectToNotificationStream();
    this.notificationService.getUnreadCount().subscribe();
    this.notificationService.requestNotificationPermission();

    // SUPPORT: підвантажити доступні організації та показати перемикач
    this.initSupportSwitcher();

    // Global search: debounced, scoped on the backend by ManagementRole
    this.searchInput$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        tap(q => this.isSearching.set(q.trim().length > 0)),
        switchMap(q => {
          const query = q.trim();
          if (!query) {
            return of<SearchResponse>({ results: {} });
          }
          return this.searchService
            .search(query)
            .pipe(catchError(() => of<SearchResponse>({ results: {} })));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        this.searchResults.set(this.flattenResults(res));
        this.isSearching.set(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.notificationService.disconnect();
  }

  private updatePageTitle(): void {
    const route = this.router.url;
    switch (route) {
      case '/dashboard':
        this.pageTitle.set('Панель управління');
        break;
      case '/legal-entities-management':
        this.pageTitle.set('Юридичні особи');
        break;
      case '/properties-management':
        this.pageTitle.set('Нерухомість');
        break;
      case '/assets-management':
        this.pageTitle.set('Активи');
        break;
      case '/officials-management':
        this.pageTitle.set('Посадові особи');
        break;
    }
  }

  protected onSearch(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.searchQuery.set(value);
    this.showSuggestions.set(value.trim().length > 0);
    this.searchInput$.next(value);
  }

  protected onSearchFocus() {
    this.isSearchFocused.set(true);
    if (this.searchQuery().trim()) {
      this.showSuggestions.set(true);
    }
  }

  protected onSearchBlur() {
    // Delay to allow click on suggestions
    setTimeout(() => {
      this.isSearchFocused.set(false);
      this.showSuggestions.set(false);
    }, 200);
  }

  private flattenResults(res: SearchResponse): SearchSuggestion[] {
    const groups = res.results ?? {};
    return Object.keys(groups)
      .sort((a, b) => this.typeRank(a) - this.typeRank(b))
      .flatMap(type =>
        (groups[type] ?? []).map(hit => ({
          entityType: hit.entityType,
          entityId: hit.entityId,
          displayText: hit.displayText,
          typeLabel: TYPE_LABELS[hit.entityType] ?? hit.entityType,
          iconName: iconFor(hit.entityType, hit.subtype),
        }))
      );
  }

  private typeRank(type: string): number {
    const i = TYPE_ORDER.indexOf(type);
    return i === -1 ? TYPE_ORDER.length : i;
  }

  protected selectSuggestion(suggestion: SearchSuggestion): void {
    this.searchQuery.set('');
    this.searchResults.set([]);
    this.showSuggestions.set(false);
    this.isSearchFocused.set(false);
    const base = DETAIL_ROUTES[suggestion.entityType];
    if (base) {
      this.router.navigate([base, suggestion.entityId]);
    }
  }

  /**
   * Support: завантажує організації й показує перемикач. Якщо активну ще не обрано —
   * авто-обирає першу зі списку (інакше context-ендпоінти віддаватимуть 403).
   * Якщо збережена активна організація більше не в списку доступів — теж скидаємо на першу.
   */
  private initSupportSwitcher(): void {
    if (!this.authService.isSupport()) {
      return;
    }
    this.isSupport.set(true);

    this.supportService
      .getOrganizations()
      .pipe(
        catchError(() => of<SupportAccessOut[]>([])),
        takeUntil(this.destroy$)
      )
      .subscribe(orgs => {
        this.organizations.set(orgs);
        if (orgs.length === 0) {
          return;
        }
        const current = this.activeOrgService.get();
        const stillValid =
          current && orgs.some(o => o.type === current.type && o.organizationId === current.organizationId);
        if (!stillValid) {
          this.activeOrgService.set(this.toActiveOrg(orgs[0]));
        }
      });
  }

  protected selectOrganization(org: SupportAccessOut): void {
    const current = this.activeOrgService.get();
    if (current && current.type === org.type && current.organizationId === org.organizationId) {
      return;
    }
    this.activeOrgService.set(this.toActiveOrg(org));
    // Перезавантажуємо, щоб усі view-компоненти перезапитали дані з новим заголовком.
    window.location.reload();
  }

  private toActiveOrg(org: SupportAccessOut): ActiveOrg {
    return {
      type: org.type,
      organizationId: org.organizationId,
      organizationName: org.organizationName,
    };
  }

  protected onMenuToggle() {
    this.menuToggle.emit();
  }

  protected toggleNotificationPanel(): void {
    this.showNotificationPanel.update(v => !v);
  }

  protected closeNotificationPanel(): void {
    this.showNotificationPanel.set(false);
  }

  protected goToProfile(): void {
    this.router.navigate(['/profile']);
  }

  protected async logout(): Promise<void> {
    const result = await this.authService.signOut();
    if (result.success) {
      this.router.navigate(['/login']);
    }
  }
}
